// ── The sign-outs of this tab (#666) ─────────────────────────────────────────
// A leaf module: it has NO imports. `api/client.ts`, `store/auth.ts`, the
// hooks and the pages import it, and no import cycle can start here
// (`api/client.ts` imports `store/auth.ts`).
//
// `logout()` in `api/client.ts` is the only caller of `beginSignOut()` and
// `endSignOut()`. The sign-out generation goes up by one when a sign-out
// begins, and again when it ends. A read gets a stamp (the generation)
// before it begins. When the answer arrives, the answer is old if a sign-out
// runs now, or if a sign-out began or ended after the stamp. An old answer
// must not write the user, and its 401 is not an expired session.
//
// A user id cannot do this job. The read at the page load can find a stale
// user blob with another id, and the cookie session then wins
// (`agent-docs/auth.md` § Client-side session state).
// ─────────────────────────────────────────────────────────────────────────────

let running = 0;
let generation = 0;

/** The stamp of a read: the sign-out generation now. Get it before the read begins. */
export function signOutStamp(): number {
  return generation;
}

/** True when a sign-out runs now, or when a sign-out began or ended after `stamp`. */
export function signOutSince(stamp: number): boolean {
  return running > 0 || generation !== stamp;
}

/** True while a sign-out runs. */
export function signOutRunning(): boolean {
  return running > 0;
}

/** `logout()` calls this before its first step. */
export function beginSignOut(): void {
  running++;
  generation++;
}

/** `logout()` calls this after its last step, also when a step fails. */
export function endSignOut(): void {
  running--;
  generation++;
}

// The error of a request that a sign-out ended gets a mark. The user signs
// out, so `showError()` and `logBackgroundError()` show and record nothing
// for it. `inlineError()` records nothing and still returns the text, because
// a row uses the text to show that it failed.
const signOutRejections = new WeakSet<object>();

/** Mark the error of a request that a sign-out ended. */
export function markSignOutRejection(err: unknown): void {
  if (typeof err === 'object' && err !== null) signOutRejections.add(err);
}

/** True for the error of a request that a sign-out ended. */
export function isSignOutRejection(err: unknown): boolean {
  return typeof err === 'object' && err !== null && signOutRejections.has(err);
}
